"use client";

import { useState } from "react";
import Link from "next/link";
import Header from "@/components/common/Header";
import Footer from "@/components/common/Footer";
import StickyContactButtons from "@/components/common/StickyContactButtons";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";

interface ImagePopupProps {
  src: string;
  alt: string;
  onClose: () => void;
}

const ImagePopup = ({ src, alt, onClose }: ImagePopupProps) => (
  <div
    className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
    onClick={onClose}
  >
    <button
      onClick={onClose}
      className="absolute top-4 right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-all hover:scale-110"
      aria-label="Close popup"
    >
      <Icon name="XMarkIcon" size={32} />
    </button>
    <div className="relative max-w-6xl max-h-[90vh] w-full" onClick={(e) => e.stopPropagation()}>
      <AppImage src={src} alt={alt} className="w-full h-full object-contain rounded-lg" />
    </div>
  </div>
);

export default function SonarbanglaPage() {
  const [popupImage, setPopupImage] = useState<{ src: string; alt: string } | null>(null);

  const property = {
    title: "Sonarbangla",
    location: "Joka, Kolkata – 700104",
    type: "Land for Sale",
    status: "Available Now",
    description:
      "Prime residential plot in the Sonarbangla area of Joka. Excellent connectivity to Kolkata city center, close to schools, hospitals, and markets. Clear documentation and ready for construction.",
    images: [
      {
        id: "img_1",
        src: "/assets/images/444701890O-1767622133417-1769965986174.jpg",
        alt: "Sonarbangla project site view",
      },
      {
        id: "img_2",
        src: "/assets/images/Sonarbangla-1769966408949.jpg",
        alt: "Sonarbangla project site view",
      },
    ],
    features: [
      "Clear property documentation",
      "Excellent road connectivity",
      "Nearby Joka Metro station",
      "Developing residential area",
      "Schools and hospitals nearby",
    ],
    highlights: [
      { id: "h1", icon: "MapIcon", label: "Plot Size", value: "Contact for details" },
      { id: "h2", icon: "CurrencyRupeeIcon", label: "Price", value: "Contact for quote" },
      { id: "h3", icon: "MapPinIcon", label: "Location", value: "Joka, Kolkata" },
    ],
    details: [
      { label: "Property Type", value: "Residential Land" },
      { label: "Status", value: "Available Now" },
      { label: "Area", value: "Joka, Kolkata – 700104" },
      { label: "Nearest Landmark", value: "Joka Metro Station" },
      { label: "Documentation", value: "Clear & Ready" },
      { label: "Possession", value: "Immediate" },
    ],
  };

  return (
    <>
      <Header />
      <main>
        {/* Hero */}
        <section className="pt-20 pb-8 bg-white">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
              <Link href="/projects" className="hover:text-primary transition-colors">
                Properties
              </Link>
              <Icon name="ChevronRightIcon" size={16} />
              <span className="text-foreground font-medium">{property.title}</span>
            </div>
            <div className="flex flex-wrap items-center gap-4 mb-2">
              <span className="px-4 py-1.5 bg-secondary text-secondary-foreground rounded-full text-xs font-bold uppercase tracking-wider">
                {property.status}
              </span>
              <span className="text-xs font-bold uppercase tracking-wider text-primary">
                {property.type}
              </span>
            </div>
            <h1 className="font-serif text-5xl md:text-7xl font-bold text-foreground tracking-tight mb-4">
              {property.title}
            </h1>
            <p className="text-lg text-muted-foreground flex items-center gap-2">
              <Icon name="MapPinIcon" size={20} />
              {property.location}
            </p>
          </div>
        </section>

        {/* Images + Details */}
        <section className="py-12 bg-muted">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              {/* Images */}
              <div className="space-y-4">
                <div
                  className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-white group cursor-pointer"
                  onClick={() => setPopupImage({ src: property.images[0].src, alt: property.images[0].alt })}
                >
                  <AppImage
                    src={property.images[0].src}
                    alt={property.images[0].alt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 rounded-full p-3">
                      <Icon name="MagnifyingGlassPlusIcon" size={32} className="text-primary" />
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {property.images.slice(1).map((img) => (
                    <div
                      key={img.id}
                      className="relative aspect-[4/3] rounded-xl overflow-hidden bg-white group cursor-pointer"
                      onClick={() => setPopupImage({ src: img.src, alt: img.alt })}
                    >
                      <AppImage
                        src={img.src}
                        alt={img.alt}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 rounded-full p-2">
                          <Icon name="MagnifyingGlassPlusIcon" size={24} className="text-primary" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Details */}
              <div className="space-y-8">
                <div className="grid grid-cols-3 gap-4">
                  {property.highlights.map((h) => (
                    <div key={h.id} className="p-4 bg-white rounded-lg text-center">
                      <Icon name={h.icon as any} size={24} className="text-primary mx-auto mb-2" />
                      <p className="text-xs text-muted-foreground mb-1">{h.label}</p>
                      <p className="text-sm font-bold text-foreground">{h.value}</p>
                    </div>
                  ))}
                </div>

                <div>
                  <h2 className="font-bold text-2xl text-foreground mb-3">About This Property</h2>
                  <p className="text-muted-foreground leading-relaxed">{property.description}</p>
                </div>

                <div>
                  <h3 className="font-bold text-xl text-foreground mb-4">Key Features</h3>
                  <ul className="space-y-3">
                    {property.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <Icon name="CheckCircleIcon" size={20} className="text-primary shrink-0" />
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Link
                    href="/contact?project=sonarbangla"
                    className="flex-1 px-8 py-4 bg-primary text-primary-foreground rounded-lg font-bold text-center hover:bg-accent transition-all hover:scale-105 flex items-center justify-center gap-2"
                  >
                    <Icon name="ChatBubbleLeftRightIcon" size={20} />
                    Enquire About This Plot
                  </Link>
                  <a
                    href="tel:+917890369000"
                    className="flex-1 px-8 py-4 bg-white border-2 border-primary text-primary rounded-lg font-bold text-center hover:bg-primary hover:text-white transition-all flex items-center justify-center gap-2"
                  >
                    <Icon name="PhoneIcon" size={20} />
                    Call Now
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Property Details Table */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
            <h2 className="font-serif text-3xl font-bold text-foreground mb-8">Property Details</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {property.details.map((d, i) => (
                <div key={i} className="flex justify-between items-center p-4 bg-muted rounded-lg">
                  <span className="text-muted-foreground font-medium">{d.label}</span>
                  <span className="font-bold text-foreground">{d.value}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-primary text-primary-foreground text-center">
          <div className="container mx-auto px-4">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4">
              Interested in Sonarbangla?
            </h2>
            <p className="text-lg opacity-90 mb-8 max-w-2xl mx-auto">
              Contact us today to schedule a site visit and get complete documentation details.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/contact?project=sonarbangla"
                className="px-8 py-4 bg-white text-primary rounded-lg font-bold hover:bg-secondary transition-all hover:scale-105"
              >
                Get in Touch
              </Link>
              <Link
                href="/projects"
                className="px-8 py-4 border-2 border-white text-white rounded-lg font-bold hover:bg-white/10 transition-all"
              >
                View All Properties
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <StickyContactButtons />
      {popupImage && (
        <ImagePopup src={popupImage.src} alt={popupImage.alt} onClose={() => setPopupImage(null)} />
      )}
    </>
  );
}
